/**
 * Estado de verificación de un resultado de internet y agrupación de republicaciones.
 *
 * Verificar = el dato del caso APARECE en la página (o en una imagen, por OCR), no
 * que el aviso sea auténtico ni que tenga relación con las personas del caso.
 */

import type { IntelDocument, IntelEvidence, IntelView, WebResult } from "../../shared/contracts.js";

export type VerificationState =
  | "en_pagina"        // identificador del caso en el texto de la página
  | "nombre_en_pagina" // nombre completo en la página (identidad pendiente)
  | "ocr"              // solo en una imagen: candidato por revisar
  | "ejemplo"          // identificador del caso, pero solo en una demostración del sitio
  | "no_en_pagina"     // la página se leyó y el dato no aparece
  | "restringida"      // redes con login: solo vista previa del buscador
  | "no_abierta"       // robots.txt, bloqueo, error o tipo no soportado
  | "duplicada"        // mismo contenido que otra página ya leída
  | "sin_verificar";   // no se abrió (fuera de las páginas que se leen)

export interface Verification {
  state: VerificationState;
  label: string;
  tone: "success" | "warning" | "neutral" | "accent";
  hint: string;
  evidence: IntelEvidence[];
}

const LABEL_KIND: Record<IntelEvidence["kind"], string> = {
  plate: "patente", vin: "VIN", phone: "teléfono", email: "correo", rut: "RUT", person: "nombre",
};
const STRONG = new Set<IntelEvidence["kind"]>(["plate", "vin", "phone", "email", "rut"]);
const SCOPE = "Indica que el dato aparece; no confirma la autenticidad del aviso ni su relación con las personas del caso.";

export function docsByUrl(v: IntelView | undefined): Map<string, IntelDocument> {
  const m = new Map<string, IntelDocument>();
  for (const d of v?.documents ?? []) m.set(d.url, d);
  return m;
}

export function verificationOf(r: WebResult, docs: Map<string, IntelDocument>): Verification {
  const d = docs.get(r.url);
  if (!d) {
    return { state: "sin_verificar", label: "Vista previa sin verificar", tone: "neutral", evidence: [],
      hint: "Solo el título y el extracto que entregó el buscador; la página no se abrió." };
  }
  if (d.status === "skipped" && /login/.test(d.reason ?? "")) {
    return { state: "restringida", label: "Acceso restringido: solo vista previa", tone: "neutral", evidence: [],
      hint: "El sitio exige iniciar sesión; no se abre. Se ve solo lo que el buscador tiene indexado." };
  }
  if (d.status === "duplicate") {
    return { state: "duplicada", label: "Mismo contenido que otra página", tone: "neutral", evidence: [], hint: d.reason ?? "" };
  }
  if (d.status !== "ok") {
    return { state: "no_abierta", label: "No se pudo abrir la página", tone: "neutral", evidence: [],
      hint: `${d.reason ?? "Error al leer la página"}. Vista previa sin verificar.` };
  }
  const page = d.evidence.filter((e) => e.where === "pagina");
  const strong = page.filter((e) => STRONG.has(e.kind));
  const what = (es: IntelEvidence[]): string => [...new Set(es.map((e) => `${LABEL_KIND[e.kind]} ${e.value}`))].join(", ");
  const real = strong.filter((e) => e.detail !== "ejemplo");
  if (real.length) {
    return { state: "en_pagina", label: `Verificado en la página: ${what(real)}`, tone: "success", evidence: page, hint: SCOPE };
  }
  if (strong.length) {
    const why = [...new Set(strong.map((e) => e.note).filter(Boolean))].join("; ");
    return { state: "ejemplo", label: `En una demostración del sitio: ${what(strong)}`, tone: "accent", evidence: page,
      hint: `La página muestra el dato en una demostración${why ? ` (${why})` : ""}, p. ej. la maqueta de un producto; `
        + "suele estar en una pestaña o sección oculta y no se ve al abrir la página. El dato coincide, pero sin fecha ni contexto del caso. "
        + SCOPE };
  }
  const fullName = page.find((e) => e.kind === "person" && e.detail === "completo");
  if (fullName) {
    return { state: "nombre_en_pagina", label: "Nombre completo en la página · identidad pendiente", tone: "warning", evidence: page,
      hint: "Coincide el nombre; puede ser otra persona con el mismo nombre. " + SCOPE };
  }
  const ocr = d.evidence.filter((e) => e.where === "ocr");
  if (ocr.length) {
    return { state: "ocr", label: "Patente leída en una foto (OCR, por revisar)", tone: "accent", evidence: ocr,
      hint: "Lectura automática de una imagen: puede confundir caracteres parecidos (5/S, 2/Z). Revise la foto. " + SCOPE };
  }
  return { state: "no_en_pagina", label: "El dato no aparece en la página", tone: "warning", evidence: page,
    hint: "La página se leyó completa y no contiene el dato buscado (pudo cambiar, o la mención era de otra publicación)." };
}

/** Título comparable: sin tildes, sin el nombre del sitio al final ("… - BioBioChile"). */
function titleKey(t: string): string {
  const base = t.split(/\s[|–—-]\s(?=[^|–—-]{2,40}$)/)[0] ?? t;
  return base.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

/**
 * Republicaciones: misma nota o aviso en varios sitios (título igual, o la misma
 * página según el motor). Se muestran juntas, pero cada enlace conserva su fecha y
 * evidencia: dos notas del mismo hecho pueden aportar datos distintos. Un grupo
 * cuenta como UNA evidencia, no como varias independientes.
 */
export function groupRepublications(results: WebResult[]): { main: WebResult; copies: WebResult[] }[] {
  const groups = new Map<string, { main: WebResult; copies: WebResult[] }>();
  const out: { main: WebResult; copies: WebResult[] }[] = [];
  for (const r of results) {
    const k = titleKey(r.title);
    const g = k.split(" ").length >= 4 ? groups.get(k) : undefined;
    if (g && g.main.domain !== r.domain) {
      g.copies.push(r);
      continue;
    }
    const ng = { main: r, copies: [] as WebResult[] };
    if (k.split(" ").length >= 4 && !groups.has(k)) groups.set(k, ng);
    out.push(ng);
  }
  return out;
}
